import React, { useMemo } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

const PROVINCES_GEO_URL = "/zambia-provinces.geo.json";
const GADM_LEVEL0_URL = "/zambia-gadm0.json";
const GADM_LEVEL1_URL = "/zambia-gadm1.json";

// Heat: light green (low) -> dark green (high). Hue ~142 (green), saturation 50%, lightness 95% -> 22%
const HEAT_LIGHT = "hsl(142, 50%, 95%)";
const HEAT_DARK = "hsl(142, 55%, 22%)";

function normalizeToCanonical(rawName, canonicalList) {
  if (!rawName || !canonicalList?.length) return rawName || "";
  const n = rawName.trim().toLowerCase().replace(/\s+/g, " ");
  const match = canonicalList.find(
    (p) =>
      p.toLowerCase().replace(/-/g, " ") === n ||
      p.toLowerCase() === n.replace(/\s/g, "-")
  );
  return match ?? rawName.trim();
}

function interpolateHsl(count, maxCount) {
  if (!maxCount || count <= 0) return HEAT_LIGHT;
  const t = Math.min(1, count / maxCount);
  // Lightness 95 -> 22 as t 0 -> 1
  const L = 95 - t * 73;
  const S = 50 + t * 5;
  return `hsl(142, ${S}%, ${L}%)`;
}

export default function ZambiaMap({
  selectedProvince,
  onSelect,
  useGadmOutline = false,
  canonicalProvinceNames,
  provinceCounts = {},
  compact = false,
}) {
  const geoUrl = useGadmOutline ? GADM_LEVEL0_URL : GADM_LEVEL1_URL;
  const width = compact ? 480 : 800;
  const height = compact ? 310 : 400;

  const maxCount = useMemo(() => {
    const values = Object.values(provinceCounts).filter((n) => typeof n === "number");
    return values.length ? Math.max(...values, 1) : 1;
  }, [provinceCounts]);

  const showLegend = !useGadmOutline && Object.keys(provinceCounts).length > 0;

  return (
    <div style={{ position: "relative" }}>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 1400,
          center: [28, -14],
        }}
        width={width}
        height={height}
        style={{ width: "100%", height: "auto" }}
      >
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const rawName =
                geo.properties?.name ??
                geo.properties?.NAME_1 ??
                geo.properties?.COUNTRY ??
                "";
              const provinceName = canonicalProvinceNames
                ? normalizeToCanonical(rawName, canonicalProvinceNames)
                : rawName;
              const isOutlineOnly = useGadmOutline;
              const count = provinceCounts[provinceName] ?? 0;
              const heatFill = interpolateHsl(count, maxCount);
              const isDimmed =
                !isOutlineOnly && selectedProvince && selectedProvince !== provinceName;
              const defaultFill = isOutlineOnly
                ? "#4CAF50"
                : isDimmed
                  ? "#e0e0e0"
                  : heatFill;
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onClick={isOutlineOnly ? undefined : () => onSelect(provinceName)}
                  style={{
                    default: {
                      fill: defaultFill,
                      outline: "none",
                      stroke: "#fff",
                      strokeWidth: isOutlineOnly ? 0.8 : 0.5,
                    },
                    hover: {
                      fill: isOutlineOnly ? "#4CAF50" : isDimmed ? "#e0e0e0" : "hsl(142, 55%, 28%)",
                      outline: "none",
                      cursor: isOutlineOnly ? "default" : "pointer",
                    },
                    pressed: {
                      fill: isOutlineOnly ? "#4CAF50" : "hsl(142, 55%, 20%)",
                      outline: "none",
                    },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>
      {showLegend && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginTop: "6px",
            fontSize: "11px",
            color: "#555",
          }}
        >
          <span>Few</span>
          <div
            style={{
              width: "80px",
              height: "10px",
              borderRadius: "2px",
              background: `linear-gradient(to right, ${HEAT_LIGHT}, ${HEAT_DARK})`,
              flexShrink: 0,
            }}
          />
          <span>Many</span>
        </div>
      )}
    </div>
  );
}
